import React, { useEffect, useRef, useState } from 'react';
import { Upload, Button, Image, message, Spin } from 'antd';
import { UploadOutlined, DeleteOutlined, LoadingOutlined } from '@ant-design/icons';
import { ReactSortable } from 'react-sortablejs';
import api from '@/services/api';

const ManagementCarousel = () => {
  const [items, setItems] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [sorting, setSorting] = useState(false);
const prevItems = useRef([]);

  // Fetch slider list
  const fetchCarousel = async () => {
    setInitialLoading(true);
    try {
      const res = await api()({
        url: '/webshop/management/carousel',
        method: 'GET',
      });

      const sorted = (res.data.data || []).sort((a, b) => a.Sort - b.Sort);
      setItems(sorted);
    } catch (err) {
      console.error(err);
      message.error('Failed to load carousel');
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchCarousel();
  }, []);

  // Upload
  const handleUpload = async ({ file }) => {
    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const uploadRes = await api()({
        url: '/webshop/management/upload',
        method: 'POST',
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const imageUrl = uploadRes.data.url;

      const saveRes = await api()({
        url: '/webshop/management/carousel',
        method: 'POST',
        data: { image: imageUrl },
      });

      setItems(prev => [...prev, saveRes.data.data]);
      message.success('Image uploaded');
    } catch (err) {
      console.error(err);
      message.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Delete
  const handleRemove = async (id) => {
    setDeletingId(id);
    try {
      await api()({
        url: `/webshop/management/carousel/${id}`,
        method: 'DELETE',
      });

      setItems(prev => prev.filter(item => item.Id !== id));
      message.success('Deleted');
    } catch (err) {
      console.error(err);
      message.error('Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  // Sort
  const handleSort = async (newOrder) => {
    setSorting(true);
    try {
      await api()({
        url: `/webshop/management/carousel/order`,
        method: 'POST',
        data: {
          order: newOrder.map((item, index) => ({
            id: item.Id,
            position: index,
          })),
        },
      });
      message.success('Order saved');
    } catch (e) {
      message.error('Failed to save order');
    } finally {
      setSorting(false);
    }
  };

  return (
    <Spin spinning={initialLoading}>
      <h3>Carousel Slider Management</h3>

      <Upload.Dragger
        customRequest={handleUpload}
        multiple
        showUploadList={false}
        accept="image/*"
        disabled={uploading}
        style={{ marginBottom: 24 }}
      >
        <p className="ant-upload-drag-icon"><UploadOutlined /></p>
        <p className="ant-upload-text">
          {uploading ? <Spin indicator={<LoadingOutlined />} /> : 'Drag & drop images here or click to upload'}
        </p>
      </Upload.Dragger>
      <ReactSortable
        list={items}
        setList={(newList) => {
          // 1. Eğer sıralama değişmediyse return
          const hasChanged = newList.some((item, index) => item.Id !== (prevItems.current[index]?.Id));
          if (!hasChanged) return;

          // 2. Sıralama değiştiyse kaydet
          setItems(newList);
          prevItems.current = newList;
          handleSort(newList);
        }}
        animation={200}
        style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}
      >

        {items.map(item => (
          <div key={item.Id} style={{ position: 'relative', width: 200 }}>
            <Image
              src={item.URL || item.url}
              width={200}
              height={120}
              style={{ borderRadius: 8, objectFit: 'cover' }}
            />
            <Button
              danger
              icon={deletingId === item.Id ? <LoadingOutlined /> : <DeleteOutlined />}
              size="small"
              style={{
                position: 'absolute',
                top: 5,
                right: 5,
                background: 'rgba(255, 255, 255, 0.9)',
                border: 'none',
              }}
              onClick={() => handleRemove(item.Id)}
              disabled={deletingId === item.Id}
            />
          </div>
        ))}
      </ReactSortable>

      {sorting && (
        <div style={{ marginTop: 12 }}>
          <Spin spinning size="small" /> Saving sort order...
        </div>
      )}
    </Spin>
  );
};

export default ManagementCarousel;
